import React, { useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Grid,
    Alert,
    CircularProgress,
    FormControlLabel,
    Checkbox,
    MenuItem,
    Stack,
    Autocomplete
} from "@mui/material";
import { useAuth } from "../../hooks/useAuth";
import AdminLayout from "../AdminLayout";
import creditService from "../../services/creditService";
import { getCollectionDocuments } from "../../firebase/firestore";

const ACTIVITY_TYPES = [
    "Competition",
    "Volunteering",
    "Research",
    "Project",
    "Startup",
    "Mentoring",
    "NSIIC Program",
    "Other"
];

export default function AssignCreditsPage() {
    const { userProfile } = useAuth();
    const [formData, setFormData] = useState({
        assignedTo: "",
        amount: "",
        activityType: "",
        description: "",
        isTeamCredit: false
    });
    const [loading, setLoading] = useState(false);
    const [loadingOptions, setLoadingOptions] = useState(true);
    const [message, setMessage] = useState("");
    const [messageType, setMessageType] = useState("");
    const [students, setStudents] = useState([]);
    const [teams, setTeams] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [selectedTeam, setSelectedTeam] = useState(null);

    React.useEffect(() => {
        let active = true;
        async function loadOptions() {
            try {
                setLoadingOptions(true);
                const [allUsers, allTeams] = await Promise.all([
                    getCollectionDocuments("users"),
                    getCollectionDocuments("teams")
                ]);
                if (!active) return;
                const studentUsers = (allUsers || []).filter((u) => u.role === "student");
                const enrichedStudents = studentUsers.map((u) => ({
                    ...u,
                    searchName: `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.displayName || "Unknown Student"
                }));
                setStudents(enrichedStudents);
                const normalizedTeams = (allTeams || []).map((t) => ({
                    ...t,
                    teamName: t.teamName || t.name || "Unnamed Team",
                    leaderEmail: t.leaderEmail || "",
                    leaderName: t.leaderName || ""
                }));
                setTeams(normalizedTeams);
            } catch (error) {
                setMessageType("error");
                setMessage(error.message || "Failed to load users and teams");
            } finally {
                if (active) setLoadingOptions(false);
            }
        }
        loadOptions();
        return () => {
            active = false;
        };
    }, []);

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));
        if (name === "isTeamCredit") {
            setSelectedStudent(null);
            setSelectedTeam(null);
            setFormData((prev) => ({ ...prev, assignedTo: "" }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.assignedTo || !formData.amount || !formData.activityType) {
            setMessageType("error");
            setMessage("Please fill all required fields");
            return;
        }

        try {
            setLoading(true);
            setMessage("");

            if (formData.isTeamCredit) {
                await creditService.assignTeamCredits({
                    teamId: formData.assignedTo,
                    amount: parseInt(formData.amount),
                    activityType: formData.activityType,
                    description: formData.description,
                    assignedBy: userProfile?.uid
                });
            } else {
                await creditService.assignCredit({
                    ...formData,
                    amount: parseInt(formData.amount),
                    assignedBy: userProfile?.uid
                });
            }

            setMessageType("success");
            setMessage("Credits assigned successfully!");
            setFormData({
                assignedTo: "",
                amount: "",
                activityType: "",
                description: "",
                isTeamCredit: false
            });
            setSelectedStudent(null);
            setSelectedTeam(null);
        } catch (error) {
            setMessageType("error");
            setMessage(error.message || "Failed to assign credits");
        } finally {
            setLoading(false);
        }
    };

    return (
        <AdminLayout title="Assign Credits">
            <Box sx={{ p: 3 }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 700 }}>
                    Assign Credits to Students
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Card>
                            <CardContent>
                                <Box component="form" onSubmit={handleSubmit} noValidate>
                                    {message && (
                                        <Alert severity={messageType} sx={{ mb: 2 }}>
                                            {message}
                                        </Alert>
                                    )}

                                    {formData.isTeamCredit ? (
                                        <Autocomplete
                                            options={teams}
                                            loading={loadingOptions}
                                            value={selectedTeam}
                                            onChange={(_, option) => {
                                                setSelectedTeam(option);
                                                setFormData((prev) => ({ ...prev, assignedTo: option?.id || "" }));
                                            }}
                                            getOptionLabel={(option) => option?.teamName || ""}
                                            filterOptions={(options, state) => {
                                                const q = state.inputValue.trim().toLowerCase();
                                                if (!q) return options;
                                                return options.filter((opt) =>
                                                    `${opt.teamName || ""} ${opt.leaderName || ""} ${opt.leaderEmail || ""}`.toLowerCase().includes(q)
                                                );
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    margin="normal"
                                                    label="Search Team by Name"
                                                    required
                                                />
                                            )}
                                        />
                                    ) : (
                                        <Autocomplete
                                            options={students}
                                            loading={loadingOptions}
                                            value={selectedStudent}
                                            onChange={(_, option) => {
                                                setSelectedStudent(option);
                                                setFormData((prev) => ({ ...prev, assignedTo: option?.id || "" }));
                                            }}
                                            getOptionLabel={(option) => `${option.searchName}${option.email ? ` (${option.email})` : ""}`}
                                            filterOptions={(options, state) => {
                                                const q = state.inputValue.trim().toLowerCase();
                                                if (!q) return options;
                                                return options.filter((opt) => {
                                                    const name = String(opt.searchName || "").toLowerCase();
                                                    const email = String(opt.email || "").toLowerCase();
                                                    return name.includes(q) || email.includes(q);
                                                });
                                            }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    fullWidth
                                                    margin="normal"
                                                    label="Search Student by Name or Email"
                                                    required
                                                />
                                            )}
                                        />
                                    )}

                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Credits Amount"
                                        name="amount"
                                        type="number"
                                        value={formData.amount}
                                        onChange={handleChange}
                                        required
                                        inputProps={{ min: 1 }}
                                    />

                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        select
                                        label="Activity Type"
                                        name="activityType"
                                        value={formData.activityType}
                                        onChange={handleChange}
                                        required
                                    >
                                        {ACTIVITY_TYPES.map((type) => (
                                            <MenuItem key={type} value={type}>
                                                {type}
                                            </MenuItem>
                                        ))}
                                    </TextField>

                                    <TextField
                                        fullWidth
                                        margin="normal"
                                        label="Description"
                                        name="description"
                                        multiline
                                        rows={3}
                                        value={formData.description}
                                        onChange={handleChange}
                                    />

                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                name="isTeamCredit"
                                                checked={formData.isTeamCredit}
                                                onChange={handleChange}
                                            />
                                        }
                                        label="Distribute to all team members"
                                        sx={{ my: 1 }}
                                    />

                                    <Button
                                        type="submit"
                                        fullWidth
                                        variant="contained"
                                        sx={{ mt: 2 }}
                                        disabled={loading}
                                    >
                                        {loading ? <CircularProgress size={22} /> : "Assign Credits"}
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Card sx={{ backgroundColor: "info.lighter" }}>
                            <CardContent>
                                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                                    Activity Types
                                </Typography>
                                <Stack spacing={1}>
                                    {ACTIVITY_TYPES.map((type) => (
                                        <Typography key={type} variant="body2">
                                            • {type}
                                        </Typography>
                                    ))}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>
        </AdminLayout>
    );
}
